<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ICONS, type IconName } from "@/design/icons";
import Button from "./Button.vue";
import Icon from "./Icon.vue";

/**
 * `icon` is a name from `icons.ts`, never a text glyph (QA 9: ✉ ◷ ⊘ 🔍 drew as type).
 *
 * `failed`: the query behind this list errored, so "nothing here" would be a lie
 * (QA 17: "Ainda não há…" under "6 registros guardados"). It replaces the whole
 * empty text and its action with "couldn't load" and a retry, which emits `retry`.
 */
defineProps<{ title: string; hint?: string; icon?: IconName; failed?: boolean }>();
const emit = defineEmits<{ retry: [] }>();
const { t } = useI18n({ useScope: "global" });

// Generated screens (`BxEmptyState`) are untyped at runtime and may still pass a glyph: kept for them only.
const isIconName = (name: string): name is IconName => name in ICONS;
</script>

<template>
	<div v-if="failed" role="alert" class="flex flex-col items-center justify-center px-6 py-14 text-center">
		<p class="text-sm text-fg-muted">{{ t("state.loadFailed") }}</p>
		<Button class="mt-3" size="sm" variant="secondary" @click="emit('retry')">{{ t("action.retry") }}</Button>
	</div>
	<div v-else class="flex flex-col items-center justify-center px-6 py-14 text-center">
		<div
			v-if="icon"
			class="mb-3 flex size-10 items-center justify-center rounded-full bg-bg-muted text-lg text-fg-subtle"
			aria-hidden="true"
		>
			<Icon v-if="isIconName(icon)" :name="icon" class="size-5" />
			<template v-else>{{ icon }}</template>
		</div>
		<p class="font-serif text-lg tracking-tight text-fg">{{ title }}</p>
		<p v-if="hint" class="mt-1 max-w-xs text-xs leading-relaxed text-fg-subtle">{{ hint }}</p>
		<div class="mt-4"><slot /></div>
	</div>
</template>
